import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { API_URL } from "constants.js";
import {
  Page,
  Button as PolarisButton,
  DataTable,
  Badge as PolarisBadge,
  Spinner,
  Banner,
} from "@shopify/polaris";
import { Button } from "catalyst-ui-kit/button";
import MultipleUserSelect from "features/multi-user-select/multi-user-select";
import PrettyDate from "components/format/PrettyDate";
import DocumentSigners from "./document-signers";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchWorkSecurityRoleById,
  assignUsersToRole,
  unassignUserFromRole,
  generateWorkSecurityDocument,
  setWorkSecurityDocumentStatus,
  setWorkSecurityRoleArchived,
  WorkSecurityRole,
  RoleDocument,
  resolveWorkSecurityUploadCategoryIds,
} from "services/work-security.service";
import { useAreaScope } from "features/work-security-areas/useAreaScope";
import WorkSecurityViewSkeleton from "./work-security-view-skeleton";
import RolePositionsCard from "features/positions/RolePositionsCard";

export default function WorkSecurityRoleContainer() {
  const { companyId, roleId } = useParams<{ companyId: string; roleId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  // Same scope mechanics as the module header (PR-2 §P4): resolves from the
  // same `?area=`/localStorage sources, so a role reached via a scoped list
  // link resolves to that sphere here too, and "all spheres" navigation
  // resolves to the Select-based edit form. Only `areas`/`selectedArea` are
  // used here — the Edit modal's own read-only plaque always shows the
  // role's OWN sphere (see WorkSecurityRoleEditModal), not this selection.
  const { areas: accessibleAreas, selectedArea: headerSelectedArea } = useAreaScope(companyId);

  const [role, setRole] = useState<WorkSecurityRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleLoadError, setRoleLoadError] = useState(false);

  // User Assignment State
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAddSigners, setShowAddSigners] = useState(false);
  // Document ids whose signer table is expanded (collapsed by default to reduce scroll)
  const [expandedSignerDocs, setExpandedSignerDocs] = useState<Set<string>>(
    () => new Set()
  );
  const [showAssignedWorkers, setShowAssignedWorkers] = useState(false);
  const roleDetailsRequestRef = useRef(0);

  const loadRoleDetails = async (clearCurrentRole = false) => {
    if (!companyId || !roleId) return;
    const requestId = ++roleDetailsRequestRef.current;
    try {
      if (clearCurrentRole) {
        setRole(null);
        setRoleLoadError(false);
      }
      setLoading(true);
      const data = await fetchWorkSecurityRoleById(companyId, roleId);
      if (requestId !== roleDetailsRequestRef.current) return;
      setRole(data);
      setRoleLoadError(false);
    } catch (err) {
      if (requestId !== roleDetailsRequestRef.current) return;
      console.error("Failed to fetch work security role", err);
      const status = (err as { response?: { status?: number } }).response?.status;
      setRole(null);
      setRoleLoadError(status !== 404);
    } finally {
      if (requestId === roleDetailsRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const toggleSignerDocExpanded = (docId: string) => {
    setExpandedSignerDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const handleOpenSelectDocument = async () => {
    if (!roleId || !companyId) return;

    let currentRole = role;
    let categoryIds = resolveWorkSecurityUploadCategoryIds(currentRole);
    try {
      const freshRole = await fetchWorkSecurityRoleById(companyId, roleId);
      currentRole = freshRole;
      categoryIds = resolveWorkSecurityUploadCategoryIds(freshRole);
      setRole(freshRole);
    } catch {
      // Fall back to role already on the page.
    }

    if (!categoryIds.length) {
      setActionError(
        t("configure_upload_category_to_select", {
          defaultValue:
            "Configure the upload category in Settings before selecting an existing document.",
        })
      );
      return;
    }
    dispatch(
      setModalxOptions({
        title: t("select_existing_document", { defaultValue: "Select Document" })
      })
    );
    dispatch(setModalxComponent("WorkSecuritySelectDocumentModal"));
    dispatch(
      setModalxComponentProps({
        roleId,
        uploadCategoryIds: categoryIds,
        attachedWorkSecurityFileIds: currentRole?.attachedWorkSecurityFileIds || [],
        onSuccess: () => {
          void loadRoleDetails();
        },
      })
    );
  };

  const handleGenerateDocument = async () => {
    if (!companyId || !roleId) return;
    try {
      setGenerating(true);
      setActionError(null);
      await generateWorkSecurityDocument(companyId, roleId);
      await loadRoleDetails();
    } catch (err) {
      console.error("Failed to generate document", err);
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      setActionError(
        apiMessage ||
          t("generate_document_failed", {
            defaultValue: "Unable to generate document. Please try again.",
          })
      );
    } finally {
      setGenerating(false);
    }
  };

  // Open the exact signed PDF version a worker produced when they clicked to sign.
  const handleDownloadSignedVersion = (docFileId: string, signatureId: string) => {
    if (!companyId || !docFileId || !signatureId) return;
    window.open(
      `${API_URL}/${companyId}/files/download/${docFileId}/signature/${signatureId}`,
      "_blank"
    );
  };

  const handleSetDocumentStatus = async (documentId: string, isClosed: boolean) => {
    if (!companyId || !roleId) return;
    try {
      setActionError(null);
      await setWorkSecurityDocumentStatus(companyId, roleId, documentId, isClosed);
      await loadRoleDetails();
    } catch (err) {
      console.error("Failed to update document status", err);
      setActionError(t("update_document_status_failed", { defaultValue: "Unable to update document status. Please try again." }));
    }
  };

  // Initial / navigation load — shared request sequencing invalidates stale refreshes
  useEffect(() => {
    void loadRoleDetails(true);
  }, [companyId, roleId]);

  const assignedUserIds = role?.assignedUsers?.map((user) => user.id) || [];
  const assignedUserIdSet = new Set(assignedUserIds);
  const selectedAssignableUserIds = selectedUserIds.filter(
    (userId) => !assignedUserIdSet.has(userId)
  );

  // Handle Amendments via Global ModalX
  const handleOpenAmendModal = () => {
    if (!role) return;
    dispatch(
      setModalxOptions({
        title: t("amend_role_rules", { defaultValue: "Amend Role Rules" }),
      })
    );
    dispatch(setModalxComponent("WorkSecurityRulesAmendModal"));
    dispatch(
      setModalxComponentProps({
        roleId,
        initialRules: (role.rules || []).map((r) => r.ruleText),
        onSuccess: () => {
          void loadRoleDetails();
        },
      })
    );
  };

  // Edit role name / description via Global ModalX
  const handleOpenEditModal = () => {
    if (!role) return;
    dispatch(
      setModalxOptions({
        title: t("edit_work_security_role", { defaultValue: "Edit Role" }),
      })
    );
    dispatch(setModalxComponent("WorkSecurityRoleEditModal"));
    dispatch(
      setModalxComponentProps({
        roleId: role.id,
        initialName: role.name,
        initialDescription: role.description || "",
        initialGroupId: role.group?.id || null,
        areas: accessibleAreas,
        selectedArea: headerSelectedArea,
        initialAreaId: role.area || null,
        initialRequiredAtHiring: role.requiredAtHiring !== false,
        onSuccess: () => void loadRoleDetails(),
      })
    );
  };

  // Archive (retire) the role, then return to the list.
  const handleOpenArchiveModal = () => {
    if (!companyId || !role) return;
    dispatch(
      setModalxOptions({
        title: t("archive_role", { defaultValue: "Archive Role" }),
      })
    );
    dispatch(setModalxComponent("WorkSecurityArchiveConfirmModal"));
    dispatch(
      setModalxComponentProps({
        roleName: role.name,
        onConfirm: async () => {
          await setWorkSecurityRoleArchived(companyId, role.id, true);
          navigate(`/app/${companyId}/work-security`);
        },
      })
    );
  };

  const handleRestore = async () => {
    if (!companyId || !role) return;
    try {
      setActionError(null);
      await setWorkSecurityRoleArchived(companyId, role.id, false);
      await loadRoleDetails();
    } catch (err) {
      console.error("Failed to restore role", err);
      setActionError(
        t("failed_restore_role", {
          defaultValue: "Unable to restore role. Please try again.",
        })
      );
    }
  };

  // Handle PDF Preview via Global ModalX
  const handleOpenPdfView = (fileId?: string) => {
    if (!fileId) return;
    dispatch(
      setModalxOptions({
        title: t("view_document_preview", { defaultValue: "Document Preview" })
      })
    );
    dispatch(setModalxComponent("PdfView"));
    dispatch(
      setModalxComponentProps({
        id: fileId,
        companyId: companyId || "",
      })
    );
  };

  // Assign Users
  const handleAssignUsers = async () => {
    if (!companyId || !roleId || selectedAssignableUserIds.length === 0) return;
    try {
      setAssigning(true);
      setActionError(null);
      await assignUsersToRole(companyId, roleId, selectedAssignableUserIds);
      setSelectedUserIds([]);
      setShowAddSigners(false);
      await loadRoleDetails();
    } catch (err) {
      console.error("Failed to assign users", err);
      setActionError(t("assign_users_failed", { defaultValue: "Unable to assign users. Please try again." }));
    } finally {
      setAssigning(false);
    }
  };

  // Unassign User
  const handleUnassignUser = (userId: string, userName: string) => {
    if (!companyId || !roleId) return;

    dispatch(
      setModalxOptions({
        title: t("confirm_remove_user_title", { defaultValue: "Remove User from Role" }),
      })
    );
    dispatch(setModalxComponent("WorkSecurityUnassignConfirmModal"));
    dispatch(
      setModalxComponentProps({
        userName,
        onConfirm: async () => {
          await unassignUserFromRole(companyId, roleId, userId);
          await loadRoleDetails();
        },
      })
    );
  };

  const getDocFileId = (doc: RoleDocument): string =>
    typeof doc.file === "object" ? doc.file?.id : doc.file;

  const getDocFileName = (doc: RoleDocument): string => {
    if (typeof doc.file === "object" && (doc.file?.name || doc.file?.title)) {
      return doc.file.name || doc.file.title;
    }
    return t("latest_version_doc", { defaultValue: "Document" });
  };

  // Show the skeleton on first load, or while the loaded role still belongs to
  // a different roleId (just navigated to another role — the route reconciles
  // instead of remounting), so we never flash the previous role's data.
  if ((!role && loading) || (role && role.id !== roleId)) {
    return <WorkSecurityViewSkeleton />;
  }

  if (!role && roleLoadError) {
    return (
      <Page title={t("role_load_failed", { defaultValue: "Unable to Load Role" })}>
        <div className="text-center py-12">
          <p className="text-zinc-600">{t("role_load_failed_desc", { defaultValue: "We couldn't load this role. Please try again." })}</p>
          <Button className="mt-4" onClick={() => void loadRoleDetails(true)}>
            {t("retry", { defaultValue: "Retry" })}
          </Button>
        </div>
      </Page>
    );
  }

  if (!role) {
    return (
      <Page title={t("role_not_found", { defaultValue: "Role Not Found" })}>
        <div className="text-center py-12">
          <p className="text-zinc-600">{t("role_not_found_desc", { defaultValue: "The requested role does not exist or has been deleted." })}</p>
          <Button className="mt-4" onClick={() => navigate(`/app/${companyId}/work-security`)}>
            &larr; {t("back_to_roles", { defaultValue: "Back to Roles" })}
          </Button>
        </div>
      </Page>
    );
  }

  const documents = role.documents || [];
  const isArchived = !!role.isArchived;
  const hasRules = !!role.rules && role.rules.length > 0;
  const canGenerateDocument = hasRules && !!role.isGenerationConfigured && !isArchived;

  // The most recent document is the role's current/active safety document. When it is
  // closed there is no active document for a new worker to sign, so adding workers is
  // blocked until a fresh document is generated/attached (or the latest one is reopened).
  const latestDocument = documents.reduce<RoleDocument | null>(
    (latest, doc) =>
      !latest || new Date(doc.createdAt) > new Date(latest.createdAt) ? doc : latest,
    null
  );
  const isLatestDocumentClosed = !!latestDocument?.isClosed;

  return (
    <Page
      fullWidth
      title={role.name}
      subtitle={role.description}
      backAction={{
        content: t("back_to_roles", { defaultValue: "Back to Roles" }),
        onAction: () => navigate(`/app/${companyId}/work-security`),
      }}
      secondaryActions={
        isArchived
          ? [
              {
                content: t("restore", { defaultValue: "Restore" }),
                onAction: handleRestore,
              },
            ]
          : [
              {
                content: t("edit", { defaultValue: "Edit" }),
                onAction: handleOpenEditModal,
              },
              {
                content: t("archive", { defaultValue: "Archive" }),
                onAction: handleOpenArchiveModal,
                destructive: true,
              },
            ]
      }
    >
      {actionError && (
        <div className="mt-4">
          <Banner tone="critical" onDismiss={() => setActionError(null)}>
            {actionError}
          </Banner>
        </div>
      )}

      {isArchived && (
        <div className="mt-4">
          <Banner tone="warning">
            {t("role_archived_banner", {
              defaultValue:
                "This role is archived. Restore it to make changes or add workers.",
            })}
          </Banner>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column (lg:col-span-2): Active Security Rules */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4 sm:p-6 dark:bg-zinc-900 dark:border-zinc-800">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 mb-4 dark:border-zinc-800">
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                {t("current_security_rules", { defaultValue: "Active Rules" })}
              </h3>
              {!isArchived && (
                <button
                  type="button"
                  onClick={handleOpenAmendModal}
                  className="text-blue-600 hover:text-blue-800 text-xs font-semibold bg-transparent border-0 cursor-pointer"
                >
                  {t("amend_rules", { defaultValue: "Amend Rules" })}
                </button>
              )}
            </div>

            <div className="space-y-3 pr-1 md:max-h-[320px] md:overflow-y-auto">
              {!hasRules ? (
                <p className="text-sm text-zinc-400 italic">
                  {t("no_rules_defined", { defaultValue: "No rules defined for this role yet." })}
                </p>
              ) : (
                role.rules.map((rule, idx) => (
                  <div key={rule.id} className="flex gap-2 p-3 bg-zinc-50 dark:bg-zinc-800/30 rounded-lg text-sm border border-zinc-100 dark:border-zinc-800">
                    <span className="font-bold text-zinc-400 shrink-0">#{idx + 1}</span>
                    <span className="text-zinc-700 dark:text-zinc-300 leading-relaxed">{rule.ruleText}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* «Pareigos» that bring this pareigybė — editable by managers of
              its sphere (positions catalogue §6). */}
          {companyId && roleId && (
            <div className="mt-6">
              <RolePositionsCard
                companyId={companyId}
                roleId={roleId}
                canEdit={
                  !isArchived &&
                  !!role.area &&
                  accessibleAreas.some((area) => area.id === role.area)
                }
              />
            </div>
          )}
        </div>

        {/* Right Column (lg:col-span-1): Assigned Workers roster */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4 sm:p-6 dark:bg-zinc-900 dark:border-zinc-800">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 mb-4 dark:border-zinc-800">
              <div className="font-semibold text-sm text-zinc-950 dark:text-white">
                {t("assigned_users", { defaultValue: "Assigned Workers" })}
                {role.assignedUsers && role.assignedUsers.length > 0 ? (
                  <span className="ml-2 font-normal text-zinc-400">
                    ({role.assignedUsers.length})
                  </span>
                ) : null}
              </div>
              <PolarisButton
                variant="plain"
                disabled={isArchived || isLatestDocumentClosed}
                onClick={() => setShowAddSigners(!showAddSigners)}
              >
                {t("add_more_signers", { defaultValue: "Add Workers" })}
              </PolarisButton>
            </div>

            {isLatestDocumentClosed && (
              <p className="text-xs text-zinc-500 italic mb-3">
                {t("add_workers_disabled_closed_document", {
                  defaultValue:
                    "The latest document is closed. Generate or reopen a document to add more workers.",
                })}
              </p>
            )}

            {showAddSigners && !isLatestDocumentClosed && (
              <div className="mb-6 p-4 bg-zinc-50 border border-zinc-200 rounded-lg dark:bg-zinc-800/30 dark:border-zinc-800 space-y-3">
                <MultipleUserSelect
                  value={selectedUserIds}
                  onChange={setSelectedUserIds}
                  label="select_users_to_assign"
                  excludedUserIds={assignedUserIds}
                  isUserGuest={false}
                  showPosition
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUserIds([]);
                      setShowAddSigners(false);
                    }}
                    className="inline-flex items-center justify-center gap-x-2 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 cursor-pointer max-md:min-h-11"
                  >
                    {t("cancel", { defaultValue: "Cancel" })}
                  </button>
                  <button
                    type="button"
                    onClick={handleAssignUsers}
                    disabled={assigning || selectedAssignableUserIds.length === 0}
                    className="relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border border-transparent bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 cursor-pointer max-md:min-h-11"
                  >
                    {assigning ? t("assigning", { defaultValue: "Assigning..." }) : t("assign", { defaultValue: "Assign" })}
                  </button>
                </div>
              </div>
            )}

            {!role.assignedUsers || role.assignedUsers.length === 0 ? (
              <p className="text-xs text-zinc-500 italic py-4 text-center">
                {t("no_users_assigned", { defaultValue: "No users currently assigned to this role." })}
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-zinc-600 dark:text-zinc-400">
                    <span className="font-semibold text-zinc-800 dark:text-zinc-200">
                      {role.assignedUsers.length}
                    </span>{" "}
                    {t("workers_assigned", {
                      defaultValue: "workers assigned",
                    })}
                    {!showAssignedWorkers && role.assignedUsers.length > 0 ? (
                      <span className="text-zinc-400">
                        {" — "}
                        {role.assignedUsers
                          .slice(0, 3)
                          .map((u) => u.name)
                          .join(", ")}
                        {role.assignedUsers.length > 3
                          ? ` ${t("and_n_more", {
                              defaultValue: "+{{count}} more",
                              count: role.assignedUsers.length - 3,
                            })}`
                          : ""}
                      </span>
                    ) : null}
                  </p>
                  <PolarisButton
                    variant="plain"
                    onClick={() => setShowAssignedWorkers((prev) => !prev)}
                  >
                    {showAssignedWorkers
                      ? t("hide_users", { defaultValue: "Hide users" })
                      : t("show_users", { defaultValue: "Show users" })}
                  </PolarisButton>
                </div>

                {showAssignedWorkers && (
                  <div className="max-h-[280px] overflow-y-auto">
                    <DataTable
                      columnContentTypes={["text", "text", "text"]}
                      headings={[
                        t("Name", { defaultValue: "Name" }),
                        t("position", { defaultValue: "Position" }),
                        t("Action", { defaultValue: "Action" }),
                      ]}
                      rows={role.assignedUsers.map((user) => [
                        user.name,
                        user.position || "-",
                        <PolarisButton
                          variant="plain"
                          tone="critical"
                          disabled={isArchived}
                          onClick={() => handleUnassignUser(user.id, user.name)}
                        >
                          {t("remove", { defaultValue: "Remove" })}
                        </PolarisButton>,
                      ])}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documents section (full width) — each document is signed independently */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-zinc-200 p-4 sm:p-6 dark:bg-zinc-900 dark:border-zinc-800">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 pb-3 mb-4 dark:border-zinc-800">
          <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
            {t("documents", { defaultValue: "Documents" })}
            <span className="ml-2 text-sm font-normal text-zinc-400">({documents.length})</span>
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {canGenerateDocument && (
              <button
                type="button"
                onClick={handleGenerateDocument}
                disabled={generating}
                className="relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border border-transparent bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 cursor-pointer max-md:min-h-11"
              >
                {generating ? (
                  <div className="flex items-center gap-2">
                    <Spinner size="small" />
                    <span>{t("generating", { defaultValue: "Generating..." })}</span>
                  </div>
                ) : (
                  t("generate_pdf_document", { defaultValue: "Generate PDF Document" })
                )}
              </button>
            )}
            <button
              type="button"
              onClick={handleOpenSelectDocument}
              disabled={generating || isArchived}
              className="relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-500 disabled:opacity-50 cursor-pointer max-md:min-h-11"
            >
              {t("select_existing_document", { defaultValue: "Select Document" })}
            </button>
          </div>
        </div>

        {!hasRules ? (
          <p className="text-xs text-red-500 mb-4">
            {t("add_rule_to_generate", { defaultValue: "Please add at least one rule to enable generation." })}
          </p>
        ) : !role.isGenerationConfigured ? (
          <p className="text-xs text-amber-600 mb-4">
            {t("configure_generation_settings_to_generate", {
              defaultValue:
                "Configure document generation settings (template, series, document type, and category) in Settings before generating PDF documents.",
            })}
          </p>
        ) : null}

        {!role.isUploadConfigured && (
          <p className="text-xs text-amber-600 mb-4">
            {t("configure_upload_category_to_select", {
              defaultValue:
                "Configure the upload category in Settings before selecting an existing document.",
            })}
          </p>
        )}

        {documents.length === 0 ? (
          <p className="text-sm text-zinc-400 italic py-6 text-center">
            {t("no_document_yet", { defaultValue: "No procedure document generated for this role yet." })}
          </p>
        ) : (
          <div className="space-y-6">
            {documents.map((doc) => {
              const fileId = getDocFileId(doc);
              const fileName = getDocFileName(doc);
              const signers = doc.signers || [];
              const signedCount = doc.signedCount ?? signers.filter((s) => s.signatureStatus === "signed").length;
              const totalCount = doc.totalCount ?? signers.length;
              const pendingCount = totalCount - signedCount;
              const allSigned = totalCount > 0 && signedCount === totalCount;
              const isClosed = !!doc.isClosed;
              const isExpanded = expandedSignerDocs.has(doc.id);

              return (
                <div key={doc.id} className={`rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden ${isClosed ? "opacity-70" : ""}`}>
                  <div className="flex flex-col gap-3 p-4 bg-zinc-50 dark:bg-zinc-800/30 border-b border-zinc-100 dark:border-zinc-800 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <svg className="w-8 h-8 text-red-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A1 1 0 0112 2.586L15.414 6A1 1 0 0116 6.586V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-zinc-900 dark:text-white truncate" title={fileName}>
                          {fileName}
                        </h4>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {t("generated_on", { defaultValue: "Generated on" })}{" "}
                          <PrettyDate dateString={doc.createdAt} />
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:shrink-0">
                      {isClosed && (
                        <PolarisBadge tone="info">
                          {t("document_outdated", { defaultValue: "Outdated" })}
                        </PolarisBadge>
                      )}
                      <PolarisBadge tone={allSigned ? "success" : "attention"}>
                        {`${signedCount}/${totalCount} ${t("signed", { defaultValue: "signed" })}`}
                      </PolarisBadge>
                    </div>
                  </div>

                  <div className="p-4 space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenPdfView(fileId)}
                        className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-zinc-700 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 cursor-pointer max-md:min-h-11"
                      >
                        {t("view", { defaultValue: "View" })}
                      </button>
                      <a
                        href={`${API_URL}/${companyId}/files/download/${fileId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-zinc-700 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 cursor-pointer max-md:min-h-11 no-underline"
                      >
                        {t("download", { defaultValue: "Download" })}
                      </a>
                      <button
                        type="button"
                        onClick={() => navigate(`/app/${companyId}/documents/view/${fileId}`)}
                        className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-zinc-700 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 cursor-pointer max-md:min-h-11"
                      >
                        {t("details", { defaultValue: "Details" })}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetDocumentStatus(doc.id, !isClosed)}
                        disabled={isArchived}
                        className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-zinc-700 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 disabled:opacity-50 cursor-pointer max-md:mt-2 max-md:min-h-11 max-md:w-full md:ml-auto"
                      >
                        {isClosed
                          ? t("reopen_document", { defaultValue: "Reopen" })
                          : t("close_document", { defaultValue: "Close document" })}
                      </button>
                    </div>

                    {signers.length === 0 ? (
                      <p className="text-xs text-zinc-500 italic">
                        {t("no_users_assigned", { defaultValue: "No users currently assigned to this role." })}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400">
                            <span>
                              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                                {signedCount}
                              </span>{" "}
                              {t("signers_count_signed", {
                                defaultValue: "signed",
                              })}
                            </span>
                            <span className="text-zinc-300 dark:text-zinc-600">·</span>
                            <span>
                              <span className="font-semibold text-amber-700 dark:text-amber-400">
                                {pendingCount}
                              </span>{" "}
                              {t("signers_count_pending", {
                                defaultValue: "pending",
                              })}
                            </span>
                            <span className="text-zinc-300 dark:text-zinc-600">·</span>
                            <span>
                              <span className="font-semibold text-zinc-800 dark:text-zinc-200">
                                {totalCount}
                              </span>{" "}
                              {t("signers_count_total", {
                                defaultValue: "total",
                              })}
                            </span>
                          </div>
                          <PolarisButton
                            variant="plain"
                            onClick={() => toggleSignerDocExpanded(doc.id)}
                          >
                            {isExpanded
                              ? t("hide_signers", { defaultValue: "Hide signers" })
                              : t("show_signers", {
                                  defaultValue: "Show signers",
                                })}
                          </PolarisButton>
                        </div>

                        {isExpanded && (
                          <DocumentSigners
                            signers={signers}
                            companyId={companyId!}
                            roleId={roleId!}
                            documentId={doc.id}
                            canResend={!isClosed && !isArchived}
                            onDownloadSigned={(signatureId) => handleDownloadSignedVersion(fileId, signatureId)}
                            onResent={() => void loadRoleDetails()}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Page>
  );
}
